import { useEffect, useState } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import { TCourse, TCourseDocument, TLesson } from "../../../../@types";
import { apis, callApi } from "../../../../api";

export type TTeachingPreviewService = {
	listeners: (() => void)[];
	loading: boolean;
	course: TCourse | null;
	courseDocuments: TCourseDocument[];
	lessons: TLesson[];

	addEventListener: (cb: () => void) => void;
	setLoading: (loading: boolean) => void;
	setCourse: (course: TCourse) => void;
	setCourseDocuments: (courseDocuments: TCourseDocument[]) => void;
	setLessons: (lessons: TLesson[]) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;
};

export const TeachingPreviewService: TTeachingPreviewService = {
	listeners: [],
	loading: false,
	course: null,
	courseDocuments: [],
	lessons: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	setCourse(course) {
		this.course = course;
	},

	setCourseDocuments(courseDocuments) {
		this.courseDocuments = courseDocuments;
	},

	setLessons(lessons) {
		this.lessons = lessons;
	},

	clearData() {
		this.course = null;
		this.courseDocuments = [];
		this.lessons = [];
		this.loading = false;
		this.emitChanges();
	},

	/**
	 * Send new course data to all listeners
	 */
	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

/**
 * This hook should call only once
 */
export function useTeachingPreviewInit() {
	const params = useParams();
	const slug = params.slug ?? "";
	const [searchParams, setSearchParams] = useSearchParams();

	const [isNotFound, setIsNotFound] = useState(false);

	useEffect(() => {
		const getCourseBySlug = async () => {
			try {
				const response = await callApi({
					method: "GET",
					url: `${apis.MY_COURSES_BY_SLUG}/${slug}`,
				});

				if (!response) {
					return;
				}

				TeachingPreviewService.setCourse(response.course);
				TeachingPreviewService.setCourseDocuments(response.courseDocuments);
				TeachingPreviewService.setLessons(response.lessons);
				const classId = searchParams.get("class");
				if (classId === null && response.lessons.length > 0) {
					setSearchParams(
						(prev) => {
							prev.set("class", response.lessons[0].id.toString());
							return prev;
						},
						{ replace: true },
					);
				}
			} catch (error: any) {
				if (error?.response?.status === 404) {
					setIsNotFound(true);
				}
			}
		};

		const getData = async () => {
			TeachingPreviewService.setLoading(true);
			TeachingPreviewService.emitChanges();
			await getCourseBySlug();
			TeachingPreviewService.setLoading(false);
			TeachingPreviewService.emitChanges();
		};

		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slug]);

	useEffect(() => {
		return () => {
			TeachingPreviewService.clearData();
		};
	}, []);

	return { isNotFound, slug };
}

export function useTeachingPreview() {
	const [loading, setLoading] = useState(TeachingPreviewService.loading);
	const [course, setCourse] = useState<TCourse | null>(
		TeachingPreviewService.course,
	);
	const [courseDocuments, setCourseDocuments] = useState<TCourseDocument[]>(
		TeachingPreviewService.courseDocuments,
	);
	const [lessons, setLessons] = useState<TLesson[]>(
		TeachingPreviewService.lessons,
	);
	const [searchParams] = useSearchParams();
	const classId = Number(searchParams.get("class") ?? "0");
	const currentLesson = lessons.find((lesson) => lesson.id === classId);

	useEffect(() => {
		const updateData = () => {
			setLoading(TeachingPreviewService.loading);
			setCourse(TeachingPreviewService.course);
			setCourseDocuments(TeachingPreviewService.courseDocuments);
			setLessons(TeachingPreviewService.lessons);
		};

		TeachingPreviewService.addEventListener(updateData);

		return () => {
			TeachingPreviewService.removeEventListener(updateData);
		};
	}, []);

	return {
		loading,
		course,
		courseDocuments,
		lessons,
		currentLesson,
	};
}
